import { v4 as uuidv4 } from 'uuid';

export interface FileItem {
    fileItemId: string;
    pdfFileName: string;
    rawResults: string;
    parsedResults: string;
    pdfRawContent: File;
    lastProcessedDate: Date;
}

export class FilesService {
    private static instance: FilesService;
    private readonly dbName = 'filesDB';
    private readonly storeName = 'files';
    private db: IDBDatabase | null = null;

    private constructor() {
        this.initDB();
    }

    public static getInstance(): FilesService {
        if (!FilesService.instance) {
            FilesService.instance = new FilesService();
        }
        return FilesService.instance;
    }

    private initDB(): Promise<void> {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(this.dbName, 1);

            request.onerror = () => reject(request.error);
            request.onsuccess = () => {
                this.db = request.result;
                resolve();
            };

            request.onupgradeneeded = (event) => {
                const db = (event.target as IDBOpenDBRequest).result;
                if (!db.objectStoreNames.contains(this.storeName)) {
                    db.createObjectStore(this.storeName, { keyPath: 'fileItemId' });
                }
            };
        });
    }

    public async saveFile(file: File): Promise<string> {
        await this.ensureDBConnection();
        
        const fileItem: FileItem = {
            fileItemId: uuidv4(),
            pdfFileName: file.name,
            rawResults: '',
            parsedResults: '',
            pdfRawContent: file,
            lastProcessedDate: new Date()
        };

        return new Promise((resolve, reject) => {
            const transaction = this.db!.transaction([this.storeName], 'readwrite');
            const store = transaction.objectStore(this.storeName);
            const request = store.add(fileItem);

            request.onsuccess = () => resolve(fileItem.fileItemId);
            request.onerror = () => reject(request.error);
        });
    }

    public async getFile(fileItemId: string): Promise<FileItem | null> {
        await this.ensureDBConnection();

        return new Promise((resolve, reject) => {
            const transaction = this.db!.transaction([this.storeName], 'readonly');
            const store = transaction.objectStore(this.storeName);
            const request = store.get(fileItemId);

            request.onsuccess = () => resolve(request.result || null);
            request.onerror = () => reject(request.error);
        });
    }

    public async updateFileResults(fileItemId: string, rawResults: string, parsedResults: string): Promise<void> {
        await this.ensureDBConnection();

        return new Promise((resolve, reject) => {
            const transaction = this.db!.transaction([this.storeName], 'readwrite');
            const store = transaction.objectStore(this.storeName);
            
            const getRequest = store.get(fileItemId);
            getRequest.onsuccess = () => {
                const fileItem = getRequest.result;
                if (fileItem) {
                    fileItem.rawResults = rawResults;
                    fileItem.parsedResults = parsedResults;
                    fileItem.lastProcessedDate = new Date();
                    const updateRequest = store.put(fileItem);
                    updateRequest.onsuccess = () => resolve();
                    updateRequest.onerror = () => reject(updateRequest.error);
                } else {
                    reject(new Error('File not found'));
                }
            };
        });
    }

    public async getAllFiles(): Promise<FileItem[]> {
        await this.ensureDBConnection();

        return new Promise((resolve, reject) => {
            const transaction = this.db!.transaction([this.storeName], 'readonly');
            const store = transaction.objectStore(this.storeName);
            const request = store.getAll();

            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
    }

    public async deleteFile(fileItemId: string): Promise<void> {
        await this.ensureDBConnection();

        return new Promise((resolve, reject) => {
            const transaction = this.db!.transaction([this.storeName], 'readwrite');
            const store = transaction.objectStore(this.storeName);
            const request = store.delete(fileItemId);

            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.error);
        });
    }

    public async deleteAllFiles(): Promise<void> {
        await this.ensureDBConnection();

        return new Promise((resolve, reject) => {
            const transaction = this.db!.transaction([this.storeName], 'readwrite');
            const store = transaction.objectStore(this.storeName);
            const request = store.clear();

            request.onsuccess = () => resolve();
            request.onerror = () => reject(request.error);
        });
    }

    private async ensureDBConnection(): Promise<void> {
        if (!this.db) {
            await this.initDB();
        }
    }
}

export const filesService = FilesService.getInstance();